import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { useCart } from '../context/CartContext';

export function SideCart({ isOpen, onClose, cartItems = [], updateQuantity, removeItem }) {
  const { initiateCheckout, isCreatingCheckout, clearCart, forceRefresh } = useCart();
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50"
          >
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-medium text-berleto-greenblack">Your Cart</h2>
                <div className="flex items-center gap-2">
                  {cartItems.length > 0 && (
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to empty your cart?')) {
                          clearCart();
                          // Close cart after clearing to prevent UI freeze
                          setTimeout(() => {
                            onClose();
                          }, 200);
                        }
                      }}
                      className="p-2 hover:bg-red-50 rounded-full transition-colors text-berleto-sage hover:text-red-600"
                      title="Empty Cart"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {cartItems.length === 0 ? (
                  <p className="text-center text-berleto-sage">Your cart is empty</p>
                ) : (
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <motion.div
                        key={item.uniqueId || item.id}
                        layout
                        className="flex gap-4 bg-berleto-offwhite p-4 rounded-lg"
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-20 h-20 object-cover rounded"
                        />
                        <div className="flex-1">
                          <h3 className="font-medium text-berleto-greenblack">
                            {item.displayName || item.title}
                          </h3>
                          <p className="text-berleto-sage">
                            ${item.price ? item.price.toFixed(2) : '0.00'}
                            {item.isSubscription && (
                              <span className="text-berleto-green text-xs ml-2">
                                (10% subscription discount)
                              </span>
                            )}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => updateQuantity(item.uniqueId || item.id, Math.max(1, item.quantity - 1))}
                              className="p-1 hover:bg-white rounded disabled:opacity-50"
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="px-3 py-1 bg-white rounded">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.uniqueId || item.id, item.quantity + 1)}
                              className="p-1 hover:bg-white rounded"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
              {cartItems.length > 0 && (
                <div className="border-t p-6 space-y-4">
                  <div className="flex justify-between text-lg">
                    <span className="text-berleto-sage">Subtotal</span>
                    <span className="font-medium text-berleto-greenblack">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      className="bg-berleto-rust hover:bg-berleto-rust/90 disabled:opacity-50"
                      size="lg"
                      onClick={initiateCheckout}
                      disabled={isCreatingCheckout}
                    >
                      {isCreatingCheckout ? 'Creating...' : 'Checkout'}
                    </Button>
                    <Button 
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        forceRefresh();
                        onClose();
                      }}
                      className="border-berleto-sage text-berleto-sage hover:bg-berleto-sage/10 text-sm"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </Button>
                  </div>
                  <p className="text-center text-sm text-berleto-sage">
                    USA shipping & taxes calculated at checkout
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}